import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, ShieldCheck, Loader2 } from "lucide-react";
import { sendContactMessage } from "../lib/api";
import { dictionaries } from "../lib/i18n";
import { FEEDBACK, TRUST_NOTES } from "../data/feedback";
import type { UiLang } from "../lib/types";

/**
 * Baren nederst til høyre: hva andre sier, og et sted å si noe selv.
 *
 * ── Hva som ble bedt om ────────────────────────────────────────────────────
 *
 * «En tilbakemeldingsbar nederst, kanskje nede til høyre, med en karusell der
 * alle tilbakemeldingene beveger seg — og jeg vil at spørsmålet skal dukke opp
 * hvis personen har vært på nettsiden lenge.»
 *
 * ── Hva som roterer i den ──────────────────────────────────────────────────
 *
 * Ekte tilbakemeldinger når de finnes, og påstander om appen som kan
 * etterprøves når de ikke gjør det. Ikke oppdiktede anmeldelser — se
 * doc-kommentaren i data/feedback.ts for hvorfor, og for hvorfor det andre er
 * et sterkere argument uansett.
 *
 * ── Hvorfor den venter ─────────────────────────────────────────────────────
 *
 * Å spørre om en tilbakemelding før noen har fått noe, er å be om en mening om
 * ingenting. Ventetiden er fire minutter *aktiv* tid: fanen må være synlig, så
 * en side som ligger glemt i bakgrunnen aldri når fram til spørsmålet.
 *
 * ── De tre tingene som gjør et slikt vindu til en plage ────────────────────
 *
 *   1. Det kommer tilbake. Et nei huskes her, i localStorage, og gjelder for
 *      godt. Det samme gjør et sendt svar: den som har svart, har svart.
 *   2. Det stjeler fokus. Ingenting fokuseres før brukeren åpner selv, og
 *      `aria-live="polite"` framfor `assertive` — en skjermleser skal fullføre
 *      setningen den er i gang med.
 *   3. Det dekker det man holder på med. Baren er sammenlagt til en linje, og
 *      på smal skjerm strekker den seg langs bunnen framfor å legge seg oppå
 *      innholdet.
 */

/** Aktiv tid på siden før spørsmålet kommer av seg selv. */
export const DWELL_MS = 4 * 60 * 1000;

/** Hvor lenge hver setning står før den neste kommer. */
export const ROTATE_MS = 7000;

const DISMISSED_KEY = "reise-planer-feedback-dismissed";

/** Om brukeren har sagt nei, eller alt har svart. Begge deler er et svar. */
function alreadyAnswered(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    /*
     * Privat vindu eller blokkerte informasjonskapsler. Da er svaret «nei, ikke
     * ennå» — det verste utfallet er at spørsmålet kommer én gang til, og det
     * er langt bedre enn at baren aldri vises for noen som har den blokkert.
     */
    return false;
  }
}

function remember(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, "1");
  } catch {
    // Ikke kritisk; valget gjelder fortsatt for denne økten.
  }
}

interface Props {
  lang: UiLang;
  /** Om meldingsskjemaet er satt opp på serveren. Se `contactEnabled`. */
  contactEnabled: boolean;
}

export function FeedbackDock({ lang, contactEnabled }: Props) {
  const t = dictionaries[lang];
  const [index, setIndex] = useState(0);
  const [asking, setAsking] = useState(false);
  const [gone, setGone] = useState(() => alreadyAnswered());
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);
  const [paused, setPaused] = useState(false);

  /** Setningene som roterer: ekte ord først, ellers det appen kan bevise. */
  const quotes = FEEDBACK.map((quote) => `«${quote.text}» — ${quote.who}`);
  const lines = quotes.length > 0 ? quotes : TRUST_NOTES.map((key) => t[key]);

  /*
   * Rotasjonen står stille når noen leser.
   *
   * `paused` settes av både mus og tastatur: en setning som bytter seg bort
   * mens man er halvveis gjennom den, er en setning man ikke fikk lest. Og med
   * `prefers-reduced-motion` bytter den ikke av seg selv i det hele tatt — for
   * noen er det ikke bare irriterende, det er det som utløser kvalmen.
   */
  useEffect(() => {
    if (paused || lines.length < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(
      () => setIndex((current) => (current + 1) % lines.length),
      ROTATE_MS,
    );
    return () => window.clearInterval(timer);
  }, [paused, lines.length]);

  /*
   * Fire minutter *aktiv* tid, ikke fire minutter på klokka.
   *
   * Telleren står stille mens fanen er skjult. Uten det ville en fane som har
   * ligget åpen siden i går sprettet opp med et spørsmål i det man kommer
   * tilbake til den — om en plan man ikke lenger husker å ha laget.
   */
  const dwelled = useRef(0);
  useEffect(() => {
    if (gone || !contactEnabled) return;
    const step = 5000;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      dwelled.current += step;
      if (dwelled.current >= DWELL_MS) {
        setAsking(true);
        window.clearInterval(timer);
      }
    }, step);
    return () => window.clearInterval(timer);
  }, [gone, contactEnabled]);

  if (gone) return null;

  const dismiss = () => {
    remember();
    setGone(true);
  };

  const send = async () => {
    if (text.trim().length < 12) {
      setFailed(true);
      return;
    }
    setSending(true);
    setFailed(false);
    try {
      /*
       * Samme endepunkt som kontaktskjemaet, med honningkrukka og alt. Et
       * eget ville vært et sted til å holde tett, og gitt robotene en dør de
       * ikke måtte se etter.
       */
      await sendContactMessage(
        { name: "", replyTo: "", message: text.trim(), company: "" },
        lang,
      );
      remember();
      setSent(true);
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <aside
      className="feedback-dock no-print"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="feedback-strip">
        <ShieldCheck size={14} className="feedback-mark" aria-hidden="true" />
        {/*
          `polite`, aldri `assertive`: en setning som roterer skal ikke avbryte
          det en skjermleser holder på med. Se punkt 2 i doc-kommentaren.
        */}
        <p className="feedback-line" aria-live="polite">
          {lines[index % lines.length]}
        </p>
        <button
          type="button"
          className="feedback-close"
          onClick={dismiss}
          aria-label={t.feedbackClose}
        >
          <X size={14} />
        </button>
      </div>

      {asking && !sent && (
        <div className="feedback-ask">
          <h4 className="feedback-ask-title">
            <MessageSquare size={14} /> {t.feedbackAsk}
          </h4>
          <p className="feedback-ask-body">{t.feedbackAskBody}</p>
          <textarea
            className="feedback-input"
            rows={3}
            value={text}
            maxLength={800}
            placeholder={t.feedbackPlaceholder}
            aria-label={t.feedbackAsk}
            onChange={(event) => {
              setText(event.target.value);
              setFailed(false);
            }}
          />
          {failed && <p className="feedback-failed">{t.contactTooShort(12)}</p>}
          <div className="feedback-actions">
            <button
              type="button"
              className="feedback-send"
              disabled={sending}
              onClick={() => void send()}
            >
              {sending ? <Loader2 size={13} className="spin" /> : null}
              {sending ? t.contactSending : t.contactSend}
            </button>
            <button type="button" className="linkish feedback-later" onClick={dismiss}>
              {t.feedbackNoThanks}
            </button>
          </div>
        </div>
      )}

      {sent && <p className="feedback-thanks">{t.contactSent}</p>}
    </aside>
  );
}
